import { Trans } from "@lingui/macro";
import {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { HamburgerMenu } from "src/icons/HamburgerMenu";
import { Logo } from "src/icons/Logo";
import { Search } from "src/icons/Search";
import { User } from "src/icons/User";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { Content } from "src/atoms/Content";
import { Menu } from "src/atoms/Menu";
import { UserMenu } from "./UserMenu";
import { openNavigationMenu, openSearch } from "./duck";
import {
  StyledNavigation,
  StyledNotificationBadge,
  StyledNavigationButton,
  StyledLoginButton,
  StyledLogo,
} from "./Navigation.styled";
import Link from "next/link";
import { useUserWishlistMeta } from "src/hooks/useUserWishlistMeta";
import { Box, Flex } from "@otrium/core";
import { useRouter } from "next/router";
import { GenderSelect } from "./GenderSelect";
import { Heart } from "src/icons/Heart";
import { theme } from "src/theme";
import { SearchBlackIcon } from "src/icons";
import { useIsScrollAtPosition } from "src/hooks/useIsScrollAtPosition";
import { CartButton } from "./CartButton";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { Locale } from "lib/Intl";
import getConfig from "next/config";
interface Props {
  className?: string;
  isLoginFlow: boolean;
  gendersRef?: RefObject<HTMLDivElement>;
  simplifiedHeader?: boolean;
}

const { publicRuntimeConfig } = getConfig();

const Navigation: FC<Props> = ({
  className,
  isLoginFlow,
  gendersRef,
  simplifiedHeader = false,
}) => {
  const router = useRouter();
  const isLoginPage = router.pathname.includes("sign-in");
  const { data: userWishlistMetaData } = useUserWishlistMeta();
  const isLoggedIn = useIsLoggedIn();
  const locale = useMemo(
    () => (publicRuntimeConfig.LOCALE as Locale) || "en",
    []
  );

  const dispatch = useDispatch();
  const handleOpenNavigationMenu = useCallback(
    () => dispatch(openNavigationMenu()),
    [dispatch]
  );
  const handleOpenSearch = useCallback(
    () => dispatch(openSearch()),
    [dispatch]
  );

  const showSearchButton = useIsScrollAtPosition({
    scrollDirection: "vertical",
    validator: (scrollPos) => scrollPos > 48,
  });

  const showNotificationBadge =
    userWishlistMetaData?.userWishlistMetaData &&
    userWishlistMetaData?.userWishlistMetaData.wishlist_product_count > 0;

  return (
    <StyledNavigation className={className}>
      <Content>
        <Flex
          justifyContent={simplifiedHeader ? "center" : "space-between"}
          height={[58, 58, 58, 64]}
        >
          {!simplifiedHeader && (
            <Flex flex="1" alignItems="center">
              <Box display={["block", "block", "block", "none"]}>
                <StyledNavigationButton
                  data-testid="navigation-button"
                  onClick={handleOpenNavigationMenu}
                >
                  <HamburgerMenu />
                </StyledNavigationButton>
              </Box>

              {!isLoginFlow && (
                <>
                  <Box ml={4} display={["block", "block", "block", "none"]}>
                    <StyledNavigationButton
                      data-testid="main-header-search-btn"
                      onClick={handleOpenSearch}
                    >
                      <Search stroke={theme.colors.tone.black} />
                    </StyledNavigationButton>
                  </Box>

                  <Box display={["none", "none", "none", "block"]}>
                    <GenderSelect locale={locale} ref={gendersRef} isInHeader />
                  </Box>
                </>
              )}
            </Flex>
          )}

          {!isLoginFlow ? <HeaderLogo locale={locale} /> : null}

          {!simplifiedHeader && (
            <>
              {!isLoginFlow ? (
                <Flex flex="1" justifyContent="flex-end">
                  <Flex
                    justifyContent="flex-end"
                    alignItems="center"
                    sx={{
                      position: "relative",
                    }}
                  >
                    {!["/not-found", "/my-profile", "/my-orders"].includes(
                      router.route
                    ) && showSearchButton ? (
                      <Box ml={4} display={["none", "none", "none", "block"]}>
                        <StyledNavigationButton
                          data-testid="main-header-search-btn"
                          onClick={handleOpenSearch}
                        >
                          <SearchBlackIcon />
                        </StyledNavigationButton>
                      </Box>
                    ) : null}

                    <Box display={["none", "none", "block"]}>
                      <Menu>
                        <Menu.Button>
                          {({ onToggle }) => (
                            <Box ml={4}>
                              <StyledNavigationButton
                                variant="secondary"
                                data-testid="main-header-my-account-button"
                                onClick={onToggle}
                              >
                                <User />
                              </StyledNavigationButton>
                            </Box>
                          )}
                        </Menu.Button>
                        <Menu.List className="navigation__userMenu">
                          <UserMenu />
                        </Menu.List>
                      </Menu>
                    </Box>

                    <Link
                      href={
                        isLoggedIn
                          ? "/favourites"
                          : "/sign-in?redirect_to=/favourites"
                      }
                      as={
                        isLoggedIn
                          ? "/favourites"
                          : "/sign-in?redirect_to=/favourites"
                      }
                      passHref
                      legacyBehavior
                    >
                      <Box
                        as="a"
                        data-testid="main-header-favourites-a"
                        ml={4}
                        display="flex"
                      >
                        <Heart
                          stroke={theme.colors.tone.black}
                          strokeWidth="2"
                        />
                        {showNotificationBadge && <StyledNotificationBadge />}
                      </Box>
                    </Link>

                    <CartButton />
                  </Flex>
                </Flex>
              ) : (
                <Flex flex="1" justifyContent="flex-end">
                  {!isLoginPage && (
                    <>
                      <Link
                        data-testid="header-login-btn"
                        href="/sign-in/"
                        passHref
                        legacyBehavior
                      >
                        <Box as="span" mt={3}>
                          <StyledLoginButton as="a" variant="secondary">
                            <Trans>Login</Trans>
                          </StyledLoginButton>
                        </Box>
                      </Link>
                      <Link href="/sign-in/" passHref legacyBehavior>
                        <Box
                          as="a"
                          display={["flex", "flex", "none"]}
                          variant="centered"
                        >
                          <User />
                        </Box>
                      </Link>
                    </>
                  )}
                </Flex>
              )}
            </>
          )}
        </Flex>
      </Content>
    </StyledNavigation>
  );
};

function HeaderLogo({ locale }: { locale: Locale }) {
  const { currentShopType } = useShopTypeContext();
  const ref = useRef(!!currentShopType);
  const [logoRouter, setLogoRouter] = useState(() =>
    currentShopType ? `/${localeFix({ gender: currentShopType })}` : "/"
  );

  useEffect(() => {
    if (!ref.current && currentShopType) {
      setLogoRouter(`/${localeFix({ gender: currentShopType })}`);
      ref.current = true;
    }
  }, [currentShopType]);

  return (
    <StyledLogo>
      <Link
        href={getI18nRoute({
          route: `${logoRouter}`,
          locale,
        })}
        prefetch={false}
      >
        <Box
          data-testid="main-header-logo"
          sx={{
            alignSelf: "center",
            display: "flex",
            justifyContent: "center",
            maxHeight: "100%",
          }}
        >
          <p className="sr-only">Otrium home page</p>
          <Logo width={98} />
        </Box>
      </Link>
    </StyledLogo>
  );
}

export { Navigation };
